import type { CoverageOption } from '../../../../../api/submissions/submission-coverages.types';
import { MAX_OPTIONS_PER_PRODUCT, canAddOption } from './quoteOptionsModel';
import { useQuoteOptions } from './useQuoteOptions';
import { cartOptionsFor, useQuoteCartVisit } from './quoteCartVisit';

type Params = {
  readonly submissionId: string | undefined;
  readonly productType: string;
  /** The count the account-keyed cart reports. Used only when there is no submission. */
  readonly accountCartCount: number;
};

export type QuoteCartStatus = {
  /** Every option on the submission, or none outside a submission. */
  readonly options: CoverageOption[];
  /** The count the cart badge shows: on a submission, this product's options in this visit's cart (`cartOptionsFor`). */
  readonly count: number;
  readonly isFull: boolean;
  /** True until a submission's options have loaded, when the count is not yet known. */
  readonly isPending: boolean;
};

/**
 * One answer to "how many are waiting to be quoted, and is there room for another". A
 * submission asks the coverage options; an account-keyed flow still asks its cart.
 */
export const useQuoteCartStatus = ({
  submissionId,
  productType,
  accountCartCount,
}: Params): QuoteCartStatus => {
  const query = useQuoteOptions(submissionId);
  const visitIds = useQuoteCartVisit(submissionId);

  if (!submissionId) {
    return {
      options: [],
      count: accountCartCount,
      isFull: accountCartCount >= MAX_OPTIONS_PER_PRODUCT,
      isPending: false,
    };
  }

  const options = query.data?.coverageOptions ?? [];
  return {
    options,
    count: cartOptionsFor(productType, options, visitIds).length,
    isFull: !canAddOption(productType, options),
    isPending: query.data === undefined && !query.isError,
  };
};
